import { useMemo } from "react";
import type { TierCardDesign, CardLayout } from "../../api/rewardsAdminHooks";
import { buildCardBackground, buildPatternOverlay } from "./helpers";

interface CardPreviewProps {
  design: TierCardDesign;
  tierName: string;
  /** Render at a smaller scale for thumbnails */
  mini?: boolean;
}

export function CardPreview({ design, tierName, mini }: CardPreviewProps) {
  const bgStyle = useMemo(() => buildCardBackground(design), [design]);
  const patternSvg = useMemo(() => buildPatternOverlay(design), [design]);
  const layout = design.layout || "classic";
  const w = mini ? "w-full" : "w-[420px]";
  const rounded = mini ? "rounded-lg" : "rounded-2xl";
  const shadow = mini ? "shadow-md" : "shadow-2xl";

  return (
    <div className={`relative ${w} overflow-hidden ${rounded} ${shadow}`} style={{ aspectRatio: "4 / 3" }}>
      {/* Background */}
      <div className="absolute inset-0" style={bgStyle} />

      {/* Pattern overlay */}
      {patternSvg && (
        <div
          className="absolute inset-0"
          style={{
            opacity: (design.pattern_opacity ?? 15) / 100,
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(patternSvg)}")`,
            backgroundRepeat: "repeat",
            backgroundSize: mini ? "16px 16px" : "24px 24px",
          }}
        />
      )}

      {/* Layout content */}
      <div className="relative flex h-full w-full">
        <LayoutContent design={design} tierName={tierName} layout={layout} mini={mini} />
      </div>
    </div>
  );
}

/* ── Logo element (shared across layouts) ── */

function LogoElement({ design, mini }: { design: TierCardDesign; mini?: boolean }) {
  const mode = design.logo_mode || "none";
  if (mode === "none") return null;

  const textColor = design.text_color || "#ffffff";

  if (mode === "image" && design.logo_image) {
    const size = mini ? "h-6 w-6" : "h-12 w-12";
    return <img src={design.logo_image} alt="" className={`${size} rounded-lg object-contain`} />;
  }

  if (mode === "text" && design.logo_text) {
    const fontSize = mini ? "text-[8px]" : "text-base";
    return (
      <span className={`${fontSize} font-bold tracking-tight`} style={{ color: textColor }}>
        {design.logo_text}
      </span>
    );
  }

  return null;
}

/* ── Layout renderers ── */

function LayoutContent({
  design,
  tierName,
  layout,
  mini,
}: {
  design: TierCardDesign;
  tierName: string;
  layout: CardLayout;
  mini?: boolean;
}) {
  const tc = design.text_color || "#ffffff";
  const p = mini ? "p-3" : "p-8";
  const titleSize = mini ? "text-sm" : "text-3xl";
  const subtitleSize = mini ? "text-[6px]" : "text-sm";
  const labelSize = mini ? "text-[5px]" : "text-sm";
  const label = design.label_text || "MEMBER CARD";
  const subtitle = design.subtitle_text || "Loyalty Program";

  switch (layout) {
    case "classic":
      return (
        <div className={`flex h-full w-full flex-col justify-between ${p}`}>
          <div className="flex items-start justify-between">
            <p className={`${labelSize} font-medium uppercase tracking-widest`} style={{ color: tc, opacity: 0.7 }}>
              {label}
            </p>
            <LogoElement design={design} mini={mini} />
          </div>
          <div>
            <p className={`${titleSize} font-bold tracking-tight`} style={{ color: tc }}>{tierName}</p>
            <p className={`mt-0.5 ${subtitleSize}`} style={{ color: tc, opacity: 0.7 }}>{subtitle}</p>
          </div>
        </div>
      );

    case "centered":
      return (
        <div className={`flex h-full w-full flex-col items-center justify-center gap-1 ${p}`}>
          <LogoElement design={design} mini={mini} />
          <p className={`${titleSize} font-bold tracking-tight text-center`} style={{ color: tc }}>{tierName}</p>
          <p className={`${subtitleSize} uppercase tracking-widest`} style={{ color: tc, opacity: 0.6 }}>{label}</p>
        </div>
      );

    case "minimal":
      return (
        <div className={`flex h-full w-full flex-col ${p}`}>
          <div className="flex-1 flex items-start justify-end">
            <LogoElement design={design} mini={mini} />
          </div>
          <div className="text-right">
            <p className={`${titleSize} font-bold tracking-tight`} style={{ color: tc }}>{tierName}</p>
            <p className={`mt-0.5 ${subtitleSize} uppercase tracking-widest`} style={{ color: tc, opacity: 0.5 }}>{label}</p>
          </div>
        </div>
      );

    case "badge":
      return (
        <div className={`flex h-full w-full flex-col items-center ${p}`}>
          <div className="flex flex-1 items-center justify-center">
            {(design.logo_mode === "image" && design.logo_image) ? (
              <img src={design.logo_image} alt="" className={mini ? "h-8 w-8" : "h-16 w-16"} style={{ borderRadius: "50%", objectFit: "contain" }} />
            ) : (design.logo_mode === "text" && design.logo_text) ? (
              <div
                className={`flex items-center justify-center rounded-full border-2 ${mini ? "h-8 w-8 text-[6px]" : "h-16 w-16 text-sm"} font-bold`}
                style={{ color: tc, borderColor: `${tc}40` }}
              >
                {design.logo_text.slice(0, 2).toUpperCase()}
              </div>
            ) : (
              <div
                className={`flex items-center justify-center rounded-full border-2 ${mini ? "h-8 w-8 text-[6px]" : "h-16 w-16 text-lg"} font-bold`}
                style={{ color: tc, borderColor: `${tc}40` }}
              >
                {tierName.slice(0, 1)}
              </div>
            )}
          </div>
          <div className="text-center">
            <p className={`${titleSize} font-bold tracking-tight`} style={{ color: tc }}>{tierName}</p>
            <p className={`mt-0.5 ${subtitleSize}`} style={{ color: tc, opacity: 0.6 }}>{label}</p>
          </div>
        </div>
      );

    case "split":
      return (
        <div className={`flex h-full w-full items-center ${p}`}>
          <div className="flex-1 min-w-0">
            <p className={`${labelSize} font-medium uppercase tracking-widest`} style={{ color: tc, opacity: 0.6 }}>{label}</p>
            <p className={`${titleSize} font-bold tracking-tight mt-1`} style={{ color: tc }}>{tierName}</p>
            <p className={`mt-0.5 ${subtitleSize}`} style={{ color: tc, opacity: 0.5 }}>{subtitle}</p>
          </div>
          <div className={`shrink-0 flex items-center justify-center ${mini ? "ml-2" : "ml-6"}`}>
            {(design.logo_mode === "image" && design.logo_image) ? (
              <img src={design.logo_image} alt="" className={mini ? "h-8 w-8" : "h-16 w-16"} style={{ borderRadius: 12, objectFit: "contain" }} />
            ) : (design.logo_mode === "text" && design.logo_text) ? (
              <span className={`${mini ? "text-[8px]" : "text-xl"} font-black tracking-tighter`} style={{ color: tc, opacity: 0.3 }}>
                {design.logo_text}
              </span>
            ) : (
              <span className={`${mini ? "text-xs" : "text-5xl"} font-black tracking-tighter`} style={{ color: tc, opacity: 0.1 }}>
                {tierName.slice(0, 2)}
              </span>
            )}
          </div>
        </div>
      );

    case "bar":
      return (
        <div className="flex h-full w-full flex-col">
          <div className={`flex-1 flex items-start justify-end ${p}`}>
            <LogoElement design={design} mini={mini} />
          </div>
          <div
            className={`${mini ? "px-3 py-2" : "px-8 py-5"}`}
            style={{ backgroundColor: `${tc}15`, backdropFilter: "blur(12px)" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`${titleSize} font-bold tracking-tight`} style={{ color: tc }}>{tierName}</p>
                <p className={`${subtitleSize}`} style={{ color: tc, opacity: 0.6 }}>{label}</p>
              </div>
              <p className={`${labelSize} uppercase tracking-widest`} style={{ color: tc, opacity: 0.4 }}>{subtitle}</p>
            </div>
          </div>
        </div>
      );
  }
}
